// src/components/SizingTool.jsx
//
// Indicative cold room sizing. The arithmetic and every coefficient live in
// src/lib/sizing.js so they can be reviewed in one place.
//
// The result is not the point on its own - the point is the button at the end,
// which hands the whole specification to the enquiry form. An enquiry that
// arrives with dimensions, temperatures and an indicative load is a brief the
// office can quote against rather than a request for a callback.

import React, { useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useInView } from "motion/react";
import { ArrowRight, Info, Snowflake, Thermometer, Wind } from "lucide-react";
import NumberField from "./NumberField";
import {
  ROOM_TYPES,
  DOOR_USAGE,
  DEFAULT_AMBIENT,
  calculate,
  toBrief,
} from "../lib/sizing";

const TYPE_KEYS = Object.keys(ROOM_TYPES);
const DOOR_KEYS = Object.keys(DOOR_USAGE);

const TYPE_ICON = {
  chiller: Thermometer,
  freezer: Snowflake,
  blast: Wind,
};

export default function SizingTool({ onUseBrief }) {
  const wrapRef = useRef(null);
  const seen = useInView(wrapRef, { once: true, margin: "-70px" });

  const [type, setType] = useState("chiller");
  const [dims, setDims] = useState({ length: 3, width: 3, height: 2.5 });
  const [doorUsage, setDoorUsage] = useState("moderate");
  const [advanced, setAdvanced] = useState(false);
  const [ambient, setAmbient] = useState(DEFAULT_AMBIENT);
  const [throughputKg, setThroughputKg] = useState("");
  const [entryTemp, setEntryTemp] = useState(25);

  const result = useMemo(
    () =>
      calculate({
        type,
        ...dims,
        ambient,
        doorUsage,
        throughputKg: throughputKg === "" ? null : throughputKg,
        entryTemp,
      }),
    [type, dims, ambient, doorUsage, throughputKg, entryTemp]
  );

  const setDim = (k) => (v) => setDims((d) => ({ ...d, [k]: v }));

  const send = () => {
    onUseBrief?.({
      subjectRef: "cold-room",
      message: toBrief(result),
    });
    document.getElementById("enquire")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="sizing" className="border-t border-line py-20 sm:py-28">
      <div ref={wrapRef} className="mx-auto max-w-6xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={seen ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-brooke-red">
            Sizing estimator
          </p>
          <h2 className="mt-3 max-w-[18ch] font-arch text-[clamp(26px,4.4vw,42px)] font-extrabold leading-[1.08] tracking-[-0.022em] text-ink">
            Work out roughly what you need
          </h2>
          <p className="mt-6 max-w-[58ch] text-[15px] leading-relaxed text-muted sm:text-base">
            Give the internal size and how the room will be used, and this
            estimates the heat load, the panel thickness and the plant capacity.
            It is a starting point for the conversation, not a design.
          </p>
        </motion.div>

        <div className="mt-12 grid gap-6 lg:grid-cols-[1fr_1fr] lg:gap-8">
          {/* --- inputs --------------------------------------------------- */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={seen ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.55, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
            className="rounded-2xl border border-line bg-surface2 p-6 sm:p-8"
          >
            <Group label="Room type" hint={ROOM_TYPES[type].blurb}>
              <div className="grid gap-2.5 sm:grid-cols-3">
                {TYPE_KEYS.map((k) => {
                  const Icon = TYPE_ICON[k];
                  return (
                    <Choice
                      key={k}
                      active={type === k}
                      onClick={() => setType(k)}
                      icon={<Icon size={14} strokeWidth={2} />}
                      title={ROOM_TYPES[k].label}
                      sub={`${ROOM_TYPES[k].roomTemp}\u2009\u00b0C`}
                    />
                  );
                })}
              </div>
            </Group>

            <Group label="Internal size" hint="metres">
              <div className="grid grid-cols-3 gap-3">
                <NumberField
                  id="sz-length"
                  label="Length"
                  min={1}
                  max={60}
                  value={dims.length}
                  onChange={setDim("length")}
                />
                <NumberField
                  id="sz-width"
                  label="Width"
                  min={1}
                  max={60}
                  value={dims.width}
                  onChange={setDim("width")}
                />
                <NumberField
                  id="sz-height"
                  label="Height"
                  min={1.8}
                  max={12}
                  value={dims.height}
                  onChange={setDim("height")}
                />
              </div>
            </Group>

            <Group label="Door usage">
              <div className="grid gap-2.5 sm:grid-cols-3">
                {DOOR_KEYS.map((k) => (
                  <Choice
                    key={k}
                    active={doorUsage === k}
                    onClick={() => setDoorUsage(k)}
                    title={DOOR_USAGE[k].label}
                    sub={DOOR_USAGE[k].blurb}
                  />
                ))}
              </div>
            </Group>

            <div className="border-t border-line pt-5">
              <button
                type="button"
                onClick={() => setAdvanced((v) => !v)}
                aria-expanded={advanced}
                className="flex items-center gap-2.5 font-mono text-[10px] uppercase tracking-[0.16em] text-subtle transition hover:text-ink"
              >
                <span
                  className={`grid h-4 w-4 place-items-center rounded-full border border-line text-[12px] leading-none transition-transform duration-300 ${
                    advanced ? "rotate-45" : ""
                  }`}
                >
                  +
                </span>
                Ambient, throughput and entry temperature
              </button>

              <AnimatePresence initial={false}>
                {advanced && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className="overflow-hidden"
                  >
                    <div className="grid gap-3 pt-5 sm:grid-cols-3">
                      <NumberField
                        id="sz-ambient"
                        label="Ambient"
                        suffix="°C"
                        min={15}
                        max={50}
                        step={1}
                        value={ambient}
                        onChange={setAmbient}
                      />
                      <NumberField
                        id="sz-throughput"
                        label="Product in"
                        suffix="kg/day"
                        min={0}
                        max={100000}
                        step={50}
                        placeholder={String(Math.round(result.inputs.mass))}
                        value={throughputKg}
                        onChange={setThroughputKg}
                      />
                      <NumberField
                        id="sz-entry"
                        label="Entry temp"
                        suffix="°C"
                        min={-30}
                        max={50}
                        step={1}
                        value={entryTemp}
                        onChange={setEntryTemp}
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>

          {/* --- result --------------------------------------------------- */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={seen ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.55, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
            className="bk-plate flex flex-col rounded-2xl border border-line p-6 shadow-[0_24px_60px_-34px_rgba(4,12,20,0.5)] sm:p-8"
          >
            <div className="flex items-start justify-between border-b border-line pb-5">
              <div>
                <p className="font-mono text-[8.5px] uppercase tracking-[0.24em] text-subtle">
                  Indicative result
                </p>
                <p className="mt-1 font-arch text-sm font-extrabold uppercase tracking-tight text-ink">
                  {result.spec.label}
                </p>
              </div>
              <div className="text-right">
                <p className="font-mono text-[8.5px] uppercase tracking-[0.18em] text-subtle">
                  Volume
                </p>
                <p className="mt-1 font-mono text-[13px] tabular-nums text-muted">
                  {result.volume} m&sup3;
                </p>
              </div>
            </div>

            {/* headline figure */}
            <div className="flex items-end justify-between border-b border-line py-8">
              <div className="flex items-baseline">
                <AnimatePresence mode="popLayout" initial={false}>
                  <motion.span
                    key={result.plantKw}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.22 }}
                    className="font-mono text-[46px] font-bold leading-none tabular-nums tracking-tight text-led sm:text-[58px]"
                  >
                    {result.plantKw}
                  </motion.span>
                </AnimatePresence>
                <span className="ml-2 font-mono text-[15px] font-bold text-led/70">
                  kW
                </span>
              </div>
              <span className="pb-1.5 text-right font-mono text-[9px] uppercase leading-relaxed tracking-[0.18em] text-subtle">
                Indicative
                <br />
                plant capacity
              </span>
            </div>

            <GroupLabel>Specification</GroupLabel>
            <dl>
              <Row label="Recommended panel" value={`${result.panel.mm} mm`} strong />
              <Row label="Envelope area" value={`${result.envelope} m\u00b2`} />
              <Row label="Temperature difference" value={`${result.deltaT} °C`} />
            </dl>

            <GroupLabel>Load breakdown</GroupLabel>
            <dl>
              <Bar
                label="Transmission"
                value={result.loads.transmission}
                total={result.totalKw}
              />
              <Bar
                label="Infiltration"
                value={result.loads.infiltration}
                total={result.totalKw}
              />
              <Bar label="Product" value={result.loads.product} total={result.totalKw} />
              <Bar
                label="Internal"
                value={result.loads.internal}
                total={result.totalKw}
              />
              <Row label="Total load" value={`${result.totalKw} kW`} strong />
            </dl>

            <p className="mt-6 flex gap-2.5 rounded-lg border border-line bg-bg/50 px-4 py-3 text-[12px] leading-relaxed text-subtle">
              <Info size={14} className="mt-0.5 shrink-0" />
              <span>
                An estimate for discussion only. Panel specification and plant
                selection are confirmed on a site survey, where layout, product
                and door position all change the answer.
              </span>
            </p>

            <button
              type="button"
              onClick={send}
              className="group mt-5 inline-flex min-h-[52px] w-full items-center justify-center gap-2.5 rounded-full bg-brooke-red px-8 font-mono text-[10px] uppercase tracking-[0.18em] text-white shadow-[0_10px_28px_-10px_rgba(231,0,26,0.8)] transition hover:bg-brooke-redDark"
            >
              Send this to Brooke
              <ArrowRight
                size={14}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
function Group({ label, hint, children }) {
  return (
    <div className="mb-7 last:mb-0">
      <div className="mb-3 flex items-baseline justify-between gap-4">
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-subtle">
          {label}
        </span>
        {hint && <span className="truncate text-[12px] text-subtle">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function GroupLabel({ children }) {
  return (
    <p className="pb-2 pt-6 font-mono text-[8.5px] uppercase tracking-[0.24em] text-subtle">
      {children}
    </p>
  );
}

// The selected state is a solid left marker rather than a wash, so it stays
// obvious in both themes without tinting the whole card.
function Choice({ active, onClick, icon, title, sub }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
           className={`rounded-lg border px-4 py-3 text-left transition ${
        active
          ? "border-brooke-red bg-brooke-red/[0.07] ring-1 ring-brooke-red"
          : "border-line hover:border-brooke-red/50 hover:bg-brooke-red/[0.03]"
      }`}
    >
      <span className="flex items-center gap-2">
        {icon && (
          <span className={active ? "text-brooke-red" : "text-subtle"}>{icon}</span>
        )}
        <span className="font-arch text-[13.5px] font-extrabold text-ink">{title}</span>
      </span>
      <span className="mt-1 block font-mono text-[9px] uppercase tracking-[0.12em] text-subtle">
        {sub}
      </span>
    </button>
  );
}

function Row({ label, value, strong }) {
  return (
    <div className="flex items-baseline justify-between gap-6 border-b border-line py-2.5">
      <dt className="font-mono text-[9px] uppercase tracking-[0.16em] text-subtle">
        {label}
      </dt>
      <dd
        className={`text-right font-mono text-[11.5px] tabular-nums tracking-[0.03em] ${
          strong ? "font-bold text-ink" : "text-muted"
        }`}
      >
        {value}
      </dd>
    </div>
  );
}

// Each component of the load carries a share bar, so it is obvious at a glance
// which one is driving the number - usually product on a freezer, and
// infiltration on a busy chiller.
function Bar({ label, value, total }) {
  const pct = total > 0 ? Math.min(100, (value / total) * 100) : 0;

  return (
    <div className="border-b border-line py-2.5">
      <div className="flex items-baseline justify-between gap-6">
        <dt className="font-mono text-[9px] uppercase tracking-[0.16em] text-subtle">
          {label}
        </dt>
        <dd className="text-right font-mono text-[11.5px] tabular-nums tracking-[0.03em] text-muted">
          {value} kW
        </dd>
      </div>
      <div className="mt-2 h-[3px] overflow-hidden rounded-full bg-line/60">
        <motion.div
          className="h-full rounded-full bg-led/70"
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        />
      </div>
    </div>
  );
}